import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FaCheck as SaveIcon, FaTimes as CloseIcon } from 'react-icons/fa';
import CreatableSelect from 'react-select/creatable';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';

import { BasicReportRowPropTypes } from './propTypes';
import { listItemPropTypes } from '../../shared/propTypes';
import FormControl from '../../shared/forms/formControl.component';
import Select from '../../shared/reactSelect/select.component';
import Input from '../../shared/forms/input/input.component';
import FormHelpers from '../../shared/forms/formHelpers';
import { fetchAccounts } from '../../shared/store/actions';
import { AccountsPropTypes } from '../../shared/propTypes/accounts';

import {
  companyCodesForETRByTagNameSelector,
  selectETRGroups,
  isSavingDataSelector,
  countryTaxTypesForCompanyCodeSelector,
  accountsSelector,
  accountsFetchingSelector,
} from './store/selectors';

import {
  addCustomETRDriver,
  updateCustomETRDriver,
  fetchCountryTaxTypesForCompanyCode,
  resetCountryTaxTypesForCompanyCode,
} from './store/actions';
import { periodOrCurrentSelector, reportingCurrencyCodeSelector } from '../../shared/store/selectors';

const getInitialState = (tagName, rowData = null) => ({
  formData: {
    companyCode: {
      value: (rowData && rowData.companyCode) || '',
      isValid: !!rowData,
    },
    countryTaxType: {
      value: '',
      isValid: !!rowData,
    },
    descr: {
      value: (rowData && rowData.descr) || '',
      isValid: !!rowData,
    },
    amount: {
      value: (rowData && rowData.amount) || 0,
      isValid: !!rowData,
    },

    tag: {
      value: tagName || '',
      isValid: !!tagName,
    },
  },
  isFormValid: !!rowData,
  triedToSubmitForm: false,
  elementsToValidate: [],
  shouldResetValues: false,
  isEditMode: !!rowData,

  isEditable: false,
});

class EditableSubrow extends PureComponent {
  static propTypes = {
    rowData: BasicReportRowPropTypes,
    countryTaxTypes: PropTypes.arrayOf(listItemPropTypes).isRequired,
    getCompanyCodesByTagName: PropTypes.func.isRequired,
    fetchCountryTaxTypesForCompanyCode: PropTypes.func.isRequired,
    closeAndFetchReport: PropTypes.func.isRequired,
    resetCountryTaxTypesForCompanyCode: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    addCustomETRDriver: PropTypes.func.isRequired,
    updateCustomETRDriver: PropTypes.func.isRequired,
    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
    reportingCurrencyCode: PropTypes.string.isRequired,
    tagName: PropTypes.string,
    etrGroups: PropTypes.arrayOf(listItemPropTypes).isRequired,
    isSavingData: PropTypes.bool.isRequired,
    selectedCountryTaxTypes: PropTypes.arrayOf(listItemPropTypes).isRequired,
    fetchAccounts: PropTypes.func.isRequired,
    accounts: AccountsPropTypes.isRequired,
    areAccountsFetching: PropTypes.bool.isRequired,
    changeEditableOnParent: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      ...getInitialState(props.tagName, props.rowData),
      areCountryTaxTypesFetching: false,
      countryTaxTypesForCompanyCode: [],
    };
  }

  componentDidMount() {
    const { accounts, fetchAccounts, fetchCountryTaxTypesForCompanyCode, period } = this.props;
    const { formData } = this.state;

    if (!accounts || accounts.length === 0) {
      fetchAccounts();
    }

    if (formData.companyCode.value) {
      this.setState({
        areCountryTaxTypesFetching: true,
      });
      fetchCountryTaxTypesForCompanyCode(period.name, formData.companyCode.value);
    }
  }

  componentDidUpdate(prevProps) {
    const { selectedCountryTaxTypes, resetCountryTaxTypesForCompanyCode, countryTaxTypes, rowData } = this.props;
    if (selectedCountryTaxTypes !== prevProps.selectedCountryTaxTypes) {
      this.selectCountryTaxType(null);
    }
    if (this.state.areCountryTaxTypesFetching
      && !isEqual(prevProps.countryTaxTypes, countryTaxTypes)) {
      this.setState({
        countryTaxTypesForCompanyCode: countryTaxTypes,
        areCountryTaxTypesFetching: false,
      });
      resetCountryTaxTypesForCompanyCode();

      if (rowData) {
        const encodedCTT = countryTaxTypes
          .find(({ label }) => label === rowData.countryTaxTypeDescription);

        if (encodedCTT) {
          this.selectCountryTaxType(encodedCTT);
        }
      }
    }
  }

  changeProperty = event => {
    FormHelpers.changeProperty({
      event,
      setState: this.setState.bind(this),
      callback: this.validateAll,
    });
  };

  changeDescription = params => {
    const { value } = params;

    FormHelpers.changeProperty({
      event: {
        target: {
          value,
          name: 'descr',
        },
      },
      setState: this.setState.bind(this),
      callback: this.validateAll,
    });
  };

  selectCompanyCode = companyCodeValue => {
    const { period, fetchCountryTaxTypesForCompanyCode } = this.props;
    FormHelpers.selectProperty('companyCode', 'value', companyCodeValue, this.setState.bind(this));

    this.setState({
      areCountryTaxTypesFetching: true,
      shouldResetValues: true,
    });
    fetchCountryTaxTypesForCompanyCode(period.name, companyCodeValue.value);

    FormHelpers.selectProperty('countryTaxType', 'value', '', this.setState.bind(this), '', () => {
      this.validate('companyCode', { isValid: true });
    });
  };

  selectCountryTaxType = value => FormHelpers.selectProperty('countryTaxType', 'value', value, this.setState.bind(this));

  selectTag = value => FormHelpers.selectProperty('tag', 'value', value, this.setState.bind(this));

  validate = (key, { isValid }) => {
    FormHelpers.validate({
      key,
      isValid,
      setState: this.setState.bind(this),
      callback: () => {
        this.setState({
          shouldResetValues: false,
        }, () => {
          this.validateAll();
        });
      },
    });
  };

  validationSubscription = (key, operation) => {
    FormHelpers.validationSubscription({
      key,
      operation,
      setState: this.setState.bind(this),
      callback: this.validateAll,
    });
  };

  validateAll = () => {
    FormHelpers.validateAll(this.setState.bind(this));
  };

  validateAmount = value => {
    if (isNaN(value) || value === '') {
      return 'Amount is required and must be a number';
    }

    if (Number(value) === '0') {
      return 'Amount cannot be 0';
    }

    return false;
  };

  save = async () => {
    const { period, addCustomETRDriver, updateCustomETRDriver, reportingCurrencyCode, rowData } = this.props;
    const { formData, isEditMode, isFormValid } = this.state;

    if (!isFormValid) {
      this.setState({
        triedToSubmitForm: true,
      });

      return;
    }

    if (isEditMode) {
      await updateCustomETRDriver({
        oldEntry: {
          manualEntryId: rowData.manualEntryId,
        },
        newEntry: {
          periodName: period.name,
          companyCode: formData.companyCode.value,
          countryTaxType: formData.countryTaxType.value,
          tag: formData.tag.value,
          amount: parseFloat(formData.amount.value),
          description: formData.descr.value,
          currency: reportingCurrencyCode,
        },
      });

      this.props.closeAndFetchReport();
      return;
    }

    await addCustomETRDriver({
      data: {
        periodName: period.name,
        companyCode: formData.companyCode.value,
        countryTaxType: formData.countryTaxType.value,
        tag: formData.tag.value,
        currency: reportingCurrencyCode,
        amount: parseFloat(formData.amount.value, 10),
        description: formData.descr.value,
      },
    });

    this.props.closeAndFetchReport();
  };

  isValidNewOption = option => option && option.label && option.label.length < 240;

  promptTextCreator = option => `Use custom description: ${option}`;

  highlightOnClick = event => event.target.select();

  changeEditable = isEditable => {
    this.props.changeEditableOnParent(isEditable);
    this.setState({ isEditable });
  };

  // setTimeout on 0ms because react-select by default tends to fire
  // onOpen events before onClose ones when opening dropdowns one after another.
  onOpen = () => setTimeout(() => this.changeEditable(true), 0);
  onClose = () => this.changeEditable(false);

  render() {
    const {
      formData,
      triedToSubmitForm,
      shouldResetValues,
      isEditable,
      countryTaxTypesForCompanyCode,
      areCountryTaxTypesFetching,
    } = this.state;

    const {
      getCompanyCodesByTagName,
      tagName,
      etrGroups,
      isSavingData,
      close,
      selectedCountryTaxTypes,
      accounts,
      areAccountsFetching,
    } = this.props;

    const filteredCountryTaxTypes = countryTaxTypesForCompanyCode
      .filter(({ value: countryTaxType }) => (
        selectedCountryTaxTypes.find(selectedCTT => selectedCTT.label === countryTaxType))
      );

    const rootClass = classNames(
      'editable-subrow-parent',
      { 'is-editable': isEditable }
    );

    return (
      <div className={rootClass}>
        <div className="editable-subrow row child-row stick-left tag-row">
          {!tagName && (
            <div className="col-6 editable-column tag-column">
              <FormControl
                onChange={this.selectTag}
                name="tag"
                label="ETR Group"
                hideLabel
                hideAsterisk
                validate={this.validate}
                validationSubscription={this.validationSubscription}
                value={formData.tag.value}
                required
                shouldReset={shouldResetValues}
                showErrorByForce={triedToSubmitForm}
              >
                <Select
                  options={etrGroups}
                  clearable={false}
                  disabled={isSavingData}
                  onOpen={this.onOpen}
                  onClose={this.onClose}
                  virtualized
                />
              </FormControl>
            </div>
          )}
        </div>
        <div className="editable-subrow row child-row stick-left">
          <div className="col-1 editable-column company-code-column">
            <FormControl
              onChange={this.selectCompanyCode}
              name="companyCode"
              label="Company Code"
              hideLabel
              hideAsterisk
              validate={this.validate}
              validationSubscription={this.validationSubscription}
              value={formData.companyCode.value}
              required
              shouldReset={shouldResetValues}
              showErrorByForce={triedToSubmitForm}
            >
              <Select
                options={getCompanyCodesByTagName(formData.tag.value)}
                clearable={false}
                disabled={isSavingData}
                onOpen={this.onOpen}
                onClose={this.onClose}
                virtualized
              />
            </FormControl>
          </div>

          <div className="col-1 editable-column text-center country-tax-column">
            <FormControl
              onChange={this.selectCountryTaxType}
              name="countryTaxType"
              label="Country Tax Type"
              hideLabel
              hideAsterisk
              validate={this.validate}
              validationSubscription={this.validationSubscription}
              value={formData.countryTaxType.value}
              required
              shouldReset={shouldResetValues}
              showErrorByForce={triedToSubmitForm}
            >
              {areCountryTaxTypesFetching
                ? <span>Loading...</span>
                : <Select
                  clearable={false}
                  disabled={isSavingData}
                  options={filteredCountryTaxTypes}
                  scrollMenuIntoView={false}
                  labelKey="label"
                  valueKey="value"
                  onOpen={this.onOpen}
                  onClose={this.onClose}
                  virtualized
                />}
            </FormControl>
          </div>

          <div className="col-4 editable-column description-column">
            <FormControl
              onChange={this.changeDescription}
              name="descr"
              label="Description"
              hideLabel
              hideAsterisk
              validate={this.validate}
              validationSubscription={this.validationSubscription}
              value={formData.descr.value}
              required
              shouldReset={shouldResetValues}
              showErrorByForce={triedToSubmitForm}
            >
              {areAccountsFetching
                ? <span>Loading...</span>
                : <Select
                  selectComponent={CreatableSelect}
                  clearable={false}
                  options={accounts}
                  isValidNewOption={this.isValidNewOption}
                  scrollMenuIntoView={false}
                  promptTextCreator={this.promptTextCreator}
                  virtualized
                  disabled={isSavingData}
                  placeholder="Enter custom description or type a number to find an account"
                  onOpen={this.onOpen}
                  onClose={this.onClose}
                />}
            </FormControl>
          </div>

          <div className="col-2 editable-column text-right">
            <FormControl
              onChange={this.changeProperty}
              name="amount"
              label="Amount"
              hideLabel
              hideAsterisk
              validate={this.validate}
              validationSubscription={this.validationSubscription}
              value={formData.amount.value}
              required
              placeholder="Amount"
              shouldReset={shouldResetValues}
              showErrorByForce={triedToSubmitForm}
              customValidation={this.validateAmount}
            >
              <Input disabled={isSavingData} type="text" onClick={this.highlightOnClick} />
            </FormControl>
          </div>

          <div className="col-2 text-right">
            <span
              className="editable-row-button mr-2"
              disabled={isSavingData}
              onClick={this.save}
            >
              <SaveIcon
                size={16}
              />
            </span>
            <span
              className="editable-row-button"
              disabled={isSavingData}
              onClick={close}
            >
              <CloseIcon
                size={16}
              />
            </span>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  function mapStateToProps(state) {
    return {
      period: periodOrCurrentSelector(state),
      countryTaxTypes: countryTaxTypesForCompanyCodeSelector(state),
      getCompanyCodesByTagName: companyCodesForETRByTagNameSelector(state),
      reportingCurrencyCode: reportingCurrencyCodeSelector(state),
      etrGroups: selectETRGroups(state),
      isSavingData: isSavingDataSelector(state),
      selectedCountryTaxTypes: state.reports.effectiveTaxRate.selectedCountryTaxTypes,
      accounts: accountsSelector(state),
      areAccountsFetching: accountsFetchingSelector(state),
    };
  },
  {
    addCustomETRDriver,
    updateCustomETRDriver,
    fetchAccounts,
    fetchCountryTaxTypesForCompanyCode,
    resetCountryTaxTypesForCompanyCode,
  }
)(EditableSubrow);
